import { CustomAttributeDefinition } from '@provision/entity-types';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { parseJsonSafely } from 'src/utils/objects';
import {
  AttributeDefinition,
  CustomAttribute,
  CustomAttributeGroup,
} from './custom-attributes';
import { CustomAttributeType } from './custom-attributes.types';

export const CUSTOM_ATTRIBUTE_TYPE_OPTIONS: readonly {
  label: string;
  id: CustomAttributeType;
}[] = [
  { id: 'dropdown', label: 'Dropdown' },
  { id: 'multi_select', label: 'Multi-Select' },
  { id: 'checkbox', label: 'Checkbox' },
  { id: 'short_text', label: 'Short Text' },
  { id: 'multi_line_text', label: 'Multi-Line Text' },
  { id: 'lookup_relational', label: 'Lookup Relational' },
  { id: 'date', label: 'Date' },
  { id: 'date_time', label: 'Date Time' },
  { id: 'url', label: 'URL' },
  { id: 'number', label: 'Number' },
];

export const CUSTOM_ATTRIBUTE_TYPE_LABELS: Record<CustomAttributeType, string> =
  Object.fromEntries(
    CUSTOM_ATTRIBUTE_TYPE_OPTIONS.map(({ id, label }) => [id, label]),
  ) as Record<CustomAttributeType, string>;

export const CUSTOM_ATTRIBUTE_TYPE_ICONS: Record<CustomAttributeType, string> = {
  dropdown: 'expand_circle_down',
  multi_select: 'checklist',
  checkbox: 'check_box',
  short_text: 'short_text',
  multi_line_text: 'notes',
  lookup_relational: 'account_tree',
  date: 'calendar_today',
  date_time: 'schedule',
  url: 'link',
  number: 'tag',
};

export function mapAttributesDefinition(customAttributes: CustomAttributeDefinition[]) {
  if (!customAttributes) {
    return [];
  }

  const attributes = customAttributes
    .map((customAttribute) => mapAttributeDefinition(customAttribute))
    .flat()
    .sort((a, b) => a.displayName.localeCompare(b.displayName));

  // if both the partner and the platform have an attribute with the same name, then we will user the partners version instead.
  return attributes
    .filter((attribute) => !attribute.deleted)
    .filter(
      (attribute, index, arr) =>
        !arr.some(
          (a, idx) =>
            a.displayName === attribute.displayName &&
            a.entity === attribute.entity &&
            idx !== index &&
            attribute.partnerId === PLATFORM_PARTNER_ID,
        ),
    );
}

export function mapAttributeDefinition(customAttribute?: CustomAttributeDefinition) {
  if (!customAttribute?.definition) {
    return [];
  }

  const definition = parseJsonSafely<AttributeDefinition>(customAttribute.definition);

  if (!definition?.attributes) {
    return [];
  }

  return Object.values(definition.attributes).map((entry) => ({
    ...entry,
    partnerId: customAttribute.partnerId,
    entity: customAttribute.entity,
    customAttributeDefinitionId: customAttribute.customAttributeDefinitionId,
    type: CUSTOM_ATTRIBUTE_TYPE_OPTIONS.find((option) => option.id === entry.type),
    isDefault: customAttribute.partnerId === PLATFORM_PARTNER_ID,
  })) as CustomAttribute[];
}

export function mapAttributesDefinitionGroups(customAttributes?: CustomAttributeDefinition[]) {
  if (!customAttributes) {
    return [];
  }

  return customAttributes
    .map((customAttribute) => mapAttributeDefinitionGroup(customAttribute))
    .flat();
}

export function mapAttributeDefinitionGroup(
  customAttribute?: CustomAttributeDefinition | null,
) {
  if (!customAttribute) {
    return [];
  }

  const definition = parseJsonSafely<AttributeDefinition>(customAttribute.definition);

  if (!definition?.groups) {
    return [];
  }

  return definition.groups.map((entry) => ({
    ...entry,
    partnerId: customAttribute.partnerId,
    entity: customAttribute.entity,
    customAttributeDefinitionId: customAttribute.customAttributeDefinitionId,
  })) as CustomAttributeGroup[];
}
